import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import Header from '../components/Header';
import Footer from '../components/Footer';
import PlanUsageBanner from '../components/PlanUsageBanner';
import { useAuth } from '../context/AuthContext';
import { 
  Calendar, Users, Clock, DollarSign, TrendingUp, 
  CalendarCheck, Plus, Settings, ArrowRight, Star, ArrowLeft 
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API_BASE_URL = `${BACKEND_URL}/api`;

const CalendarAdminDashboard = () => {
  const { user, token, logout } = useAuth();
  const navigate = useNavigate();
  
  const [stats, setStats] = useState(null);
  const [appointments, setAppointments] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [archivesCount, setArchivesCount] = useState(0);

  useEffect(() => {
    fetchDashboardData();
    fetchArchivesCount();
  }, []);

  const fetchArchivesCount = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/calendars/archives`, {
        headers: getAuthHeaders()
      });
      setArchivesCount((response.data.data || []).length);
    } catch (err) {
      setArchivesCount(0);
    }
  };

  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      
      // Fetch statistics
      const statsResponse = await axios.get(
        `${API_BASE_URL}/dashboard/calendar-admin/stats`,
        { headers: getAuthHeaders() }
      );
      
      // Fetch appointments
      const appointmentsResponse = await axios.get(
        `${API_BASE_URL}/dashboard/calendar-admin/appointments`,
        { headers: getAuthHeaders() }
      );
      
      setStats(statsResponse.data.data);
      setAppointments(appointmentsResponse.data.data);
      setError('');
    } catch (err) {
      console.error('Error fetching dashboard data:', err);
      setError('Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    // Get redirect URL before logout clears user state
    const lastCalendarSymbolicName = localStorage.getItem('lastCalendarSymbolicName');
    const isCustomerUser = user?.role === 'customer';
    
    // Determine redirect URL
    let redirectUrl = '/';
    if (isCustomerUser && lastCalendarSymbolicName) {
      redirectUrl = `/customer/login/${lastCalendarSymbolicName}`;
    }
    
    // Clear auth state
    logout();
    
    // Navigate immediately
    navigate(redirectUrl, { replace: true });
  };

  const formatDateTime = (isoString) => {
    const date = new Date(isoString);
    return date.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    });
  };

  const formatDate = (isoString) => {
    const date = new Date(isoString);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen">
        <Header />
        <div className="flex items-center justify-center py-20">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-yellow-500"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      
      <section className="py-12">
        <div className="max-w-7xl mx-auto px-6">
          {/* Back to Dashboard Link */}
          <button
            onClick={() => navigate('/dashboard')}
            className="mb-6 flex items-center text-gray-600 hover:text-gray-800 transition-colors"
          >
            <ArrowLeft className="w-5 h-5 mr-2" />
            Back to Dashboard
          </button>
          
          {/* Page Title */}
          <div className="mb-8">
            <h1 className="text-3xl font-bold text-gray-800">Display Statistics</h1>
            <p className="text-gray-600 mt-2">View your analytics, revenue, and appointments overview</p>
          </div>

          {error && (
            <div className="mb-6 p-4 bg-red-100 border border-red-400 text-red-700 rounded">
              {error}
            </div>
          )}

          {/* Plan Usage Warning Banner */}
          <PlanUsageBanner onUpgradeClick={() => navigate('/pricing')} />

          {/* Statistics Cards */}
          {stats && (
            <>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
                {/* Total Customers */}
                <div className="bg-gradient-to-br from-blue-500 to-blue-600 rounded-lg shadow-md p-6 text-white">
                  <div className="flex items-center justify-between mb-4">
                    <Users className="w-10 h-10 opacity-80" />
                    <TrendingUp className="w-6 h-6 opacity-60" />
                  </div>
                  <h3 className="text-3xl font-bold mb-1">{stats.overview.total_customers}</h3>
                  <p className="text-blue-100">Total Customers</p>
                </div>

                {/* Active Calendars */}
                <div className="bg-gradient-to-br from-purple-500 to-purple-600 rounded-lg shadow-md p-6 text-white">
                  <div className="flex items-center justify-between mb-4">
                    <Calendar className="w-10 h-10 opacity-80" />
                    <span className="text-sm bg-white bg-opacity-20 px-2 py-1 rounded">
                      {stats.overview.active_calendars}/{stats.overview.total_calendars}
                    </span>
                  </div>
                  <h3 className="text-3xl font-bold mb-1">{stats.overview.active_calendars}</h3>
                  <p className="text-purple-100">Active Calendars</p>
                </div>

                {/* Total Services */}
                <div className="bg-gradient-to-br from-green-500 to-green-600 rounded-lg shadow-md p-6 text-white">
                  <div className="flex items-center justify-between mb-4">
                    <Clock className="w-10 h-10 opacity-80" />
                  </div>
                  <h3 className="text-3xl font-bold mb-1">{stats.overview.total_services}</h3>
                  <p className="text-green-100">Services Offered</p>
                </div>

                {/* Total Revenue */}
                <div className="bg-gradient-to-br from-yellow-500 to-yellow-600 rounded-lg shadow-md p-6 text-white">
                  <div className="flex items-center justify-between mb-4">
                    <DollarSign className="w-10 h-10 opacity-80" />
                    <span className="text-sm bg-white bg-opacity-20 px-2 py-1 rounded">
                      ${stats.revenue.this_month}
                    </span>
                  </div>
                  <h3 className="text-3xl font-bold mb-1">${stats.revenue.total}</h3>
                  <p className="text-yellow-100">Total Revenue</p>
                </div>
              </div>

              {/* Appointment Stats Row */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
                <div className="bg-white rounded-lg shadow-md p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-gray-600 text-sm mb-1">Today's Appointments</p>
                      <h3 className="text-2xl font-bold text-gray-800">{stats.appointments.today_count}</h3>
                    </div>
                    <div className="bg-blue-100 p-3 rounded-full">
                      <CalendarCheck className="w-6 h-6 text-blue-600" />
                    </div>
                  </div>
                </div>

                <div className="bg-white rounded-lg shadow-md p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-gray-600 text-sm mb-1">This Week</p>
                      <h3 className="text-2xl font-bold text-gray-800">{stats.appointments.week_count}</h3>
                    </div>
                    <div className="bg-purple-100 p-3 rounded-full">
                      <Calendar className="w-6 h-6 text-purple-600" />
                    </div>
                  </div>
                </div>

                <div className="bg-white rounded-lg shadow-md p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-gray-600 text-sm mb-1">Upcoming Total</p>
                      <h3 className="text-2xl font-bold text-gray-800">{stats.appointments.upcoming_count}</h3>
                    </div>
                    <div className="bg-green-100 p-3 rounded-full">
                      <TrendingUp className="w-6 h-6 text-green-600" />
                    </div>
                  </div>
                </div>
              </div>
            </>
          )}

          {/* Main Content Grid */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Today's Appointments */}
            <div className="lg:col-span-2 bg-white rounded-lg shadow-md p-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-xl font-bold text-gray-800">Today's Appointments</h2>
                <button
                  onClick={() => navigate('/admin/calendars')}
                  className="text-yellow-600 hover:text-yellow-700 text-sm font-medium flex items-center"
                >
                  View All <ArrowRight className="w-4 h-4 ml-1" />
                </button>
              </div>

              {appointments && appointments.today.length > 0 ? (
                <div className="space-y-3">
                  {appointments.today.map((apt, index) => (
                    <div key={index} className="border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow">
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-1">
                            <h3 className="font-semibold text-gray-800">{apt.customer_name}</h3>
                            <span className={`text-xs px-2 py-1 rounded-full ${
                              apt.status === 'confirmed' ? 'bg-green-100 text-green-700' :
                              apt.status === 'pending' ? 'bg-yellow-100 text-yellow-700' :
                              'bg-gray-100 text-gray-700'
                            }`}>
                              {apt.status}
                            </span>
                          </div>
                          <p className="text-sm text-gray-600 mb-2">{apt.service_name}</p>
                          <div className="flex items-center gap-4 text-xs text-gray-500">
                            <span className="flex items-center">
                              <Clock className="w-3 h-3 mr-1" />
                              {formatDateTime(apt.start_time)}
                            </span>
                            <span>{apt.service_duration} min</span>
                            <span className="font-medium text-green-600">${apt.service_price}</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8 text-gray-500">
                  <Calendar className="w-12 h-12 mx-auto mb-2 opacity-50" />
                  <p>No appointments scheduled for today</p>
                </div>
              )}
            </div>

            {/* Popular Services & Quick Actions */}
            <div className="space-y-6">
              {/* Quick Actions */}
              <div className="bg-white rounded-lg shadow-md p-6">
                <h2 className="text-xl font-bold text-gray-800 mb-4">Quick Actions</h2>
                <div className="space-y-3">
                  <button
                    onClick={() => navigate('/admin/services')}
                    className="w-full bg-yellow-500 hover:bg-yellow-600 text-white font-semibold py-3 px-4 rounded-md transition-colors flex items-center justify-center"
                  >
                    <Plus className="w-5 h-5 mr-2" />
                    Add New Service
                  </button>
                  <button
                    onClick={() => navigate('/admin/calendars')}
                    className="w-full bg-purple-500 hover:bg-purple-600 text-white font-semibold py-3 px-4 rounded-md transition-colors flex items-center justify-center"
                  >
                    <Calendar className="w-5 h-5 mr-2" />
                    Create Calendar
                  </button>
                  <button
                    onClick={() => navigate('/admin/users')}
                    className="w-full bg-blue-500 hover:bg-blue-600 text-white font-semibold py-3 px-4 rounded-md transition-colors flex items-center justify-center"
                  >
                    <Users className="w-5 h-5 mr-2" />
                    View Customers
                  </button>
                  {archivesCount > 0 && (
                    <button
                      onClick={() => navigate('/admin/archives')}
                      className="w-full bg-gray-600 hover:bg-gray-700 text-white font-semibold py-3 px-4 rounded-md transition-colors flex items-center justify-center"
                    >
                      <Settings className="w-5 h-5 mr-2" />
                      Manage Archives ({archivesCount})
                    </button>
                  )}
                  <button
                    onClick={() => navigate('/manage-account')}
                    className="w-full bg-gray-200 hover:bg-gray-300 text-gray-800 font-semibold py-3 px-4 rounded-md transition-colors flex items-center justify-center"
                  >
                    <Settings className="w-5 h-5 mr-2" />
                    Manage Account
                  </button>
                </div>
              </div>

              {/* Popular Services */}
              {stats && stats.popular_services.length > 0 && (
                <div className="bg-white rounded-lg shadow-md p-6">
                  <h2 className="text-xl font-bold text-gray-800 mb-4">Popular Services</h2>
                  <div className="space-y-3">
                    {stats.popular_services.map((service, index) => (
                      <div key={index} className="flex items-center justify-between pb-3 border-b border-gray-100 last:border-0">
                        <div className="flex items-center">
                          <div className="bg-yellow-100 p-2 rounded-full mr-3">
                            <Star className="w-4 h-4 text-yellow-600" />
                          </div>
                          <div>
                            <p className="font-medium text-gray-800 text-sm">{service.name}</p>
                            <p className="text-xs text-gray-500">${service.price}</p>
                          </div>
                        </div>
                        <span className="bg-gray-100 text-gray-700 text-xs font-semibold px-2 py-1 rounded">
                          {service.booking_count} bookings
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Recent Bookings */}
          {appointments && appointments.recent.length > 0 && (
            <div className="mt-6 bg-white rounded-lg shadow-md p-6">
              <h2 className="text-xl font-bold text-gray-800 mb-4">Recent Bookings</h2>
              <div className="overflow-x-auto">
                <table className="min-w-full">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Customer</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Service</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Date & Time</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Calendar</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                      <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Amount</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {appointments.recent.map((apt, index) => (
                      <tr key={index} className="hover:bg-gray-50">
                        <td className="px-4 py-3 text-sm text-gray-900">{apt.customer_name}</td>
                        <td className="px-4 py-3 text-sm text-gray-600">{apt.service_name}</td>
                        <td className="px-4 py-3 text-sm text-gray-600">{formatDateTime(apt.start_time)}</td>
                        <td className="px-4 py-3 text-sm text-gray-600">{apt.calendar_name}</td>
                        <td className="px-4 py-3">
                          <span className={`inline-flex text-xs px-2 py-1 rounded-full font-medium ${
                            apt.status === 'confirmed' ? 'bg-green-100 text-green-700' :
                            apt.status === 'pending' ? 'bg-yellow-100 text-yellow-700' :
                            apt.status === 'cancelled' ? 'bg-red-100 text-red-700' :
                            'bg-gray-100 text-gray-700'
                          }`}>
                            {apt.status}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-sm text-right font-medium text-green-600">
                          ${apt.service_price}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      </section>
      
      <Footer />
    </div>
  );
};

export default CalendarAdminDashboard;
